import { useConsciousnessStore } from '../stores/modules/consciousness'
import { useOnboardingStore } from '../stores/onboarding'
import { useProvidersStore } from '../stores/providers'

export interface ServerClientConfig {
  enabled: boolean
  providerId: string
  baseUrl: string
  apiKey: string
  defaultModel: string
}

function resolveProxyOrigin(): string {
  const fromEnv = import.meta.env.VITE_LLM_PROXY_ORIGIN
  if (typeof fromEnv === 'string' && fromEnv.trim())
    return fromEnv.trim().replace(/\/$/, '')

  if (typeof window !== 'undefined') {
    return window.location.origin
  }

  return 'http://127.0.0.1:55467'
}

async function fetchClientConfig(url: string, timeoutMs = 3000): Promise<Response> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { signal: controller.signal })
  }
  finally {
    clearTimeout(timeout)
  }
}

export async function bootstrapProviderFromServer(): Promise<boolean> {
  const proxyOrigin = resolveProxyOrigin()

  let config: ServerClientConfig
  try {
    const response = await fetchClientConfig(`${proxyOrigin}/client-config`)
    if (!response.ok)
      return false

    config = await response.json() as ServerClientConfig
    if (!config.enabled || !config.providerId || !config.baseUrl || !config.apiKey)
      return false
  }
  catch {
    return false
  }

  const providersStore = useProvidersStore()
  const consciousnessStore = useConsciousnessStore()
  const onboardingStore = useOnboardingStore()

  providersStore.providers[config.providerId] = {
    ...providersStore.providers[config.providerId],
    apiKey: config.apiKey,
    baseUrl: config.baseUrl,
  }
  providersStore.markProviderAdded(config.providerId)

  consciousnessStore.activeProvider = config.providerId
  consciousnessStore.activeModel = config.defaultModel

  onboardingStore.markSetupCompleted()

  void (async () => {
    try {
      await providersStore.validateProvider(config.providerId, { force: true })
      await consciousnessStore.loadModelsForProvider(config.providerId)
    }
    catch (error) {
      console.warn('[server-provider-bootstrap] Provider validation/model prefetch failed:', error)
    }
  })()

  return true
}
